import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { typeLabels } from "@/mocks/catalog";

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (next: number) => {
      if (items.length === 0) return;
      setIndex((next + items.length) % items.length);
    },
    [items.length]
  );

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;
  const current = items[index];

  return (
    <div
      className="relative w-full overflow-hidden rounded-xl border border-primary-700/60 bg-primary-950"
      style={{ aspectRatio: "16 / 10" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 黄色在线热播推荐`}
            className="h-full w-full object-cover"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/45 to-transparent" />

      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-5 md:p-7">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-md bg-accent-500 px-2 py-0.5 text-xs font-bold text-white">
            本周焦点
          </span>
          <span className="rounded-md bg-background-50/15 px-2 py-0.5 text-xs font-medium text-background-100 backdrop-blur-sm">
            {typeLabels[current.type]} · {current.region}
          </span>
        </div>
        <h2 className="font-heading text-xl font-extrabold text-background-50 md:text-3xl">
          {current.title}
        </h2>
        <p className="line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200">
          {current.synopsis}
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <Link
            to={`/movie/${current.id}`}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
          >
            <i className="ri-play-circle-line text-lg" aria-hidden="true" />
            查看详情
          </Link>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-accent-300">
            <i className="ri-star-fill text-xs" aria-hidden="true" />
            {current.rating.toFixed(1)} 分
          </span>
          <span className="hidden items-center gap-1.5 text-sm text-background-300 sm:inline-flex">
            <i className="ri-user-star-line" aria-hidden="true" />
            {current.actors.slice(0, 2).join(" / ")}
          </span>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-primary-950/50 text-background-50 backdrop-blur-sm transition-colors hover:bg-accent-500 md:h-10 md:w-10"
      >
        <i className="ri-arrow-left-s-line text-xl" aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-primary-950/50 text-background-50 backdrop-blur-sm transition-colors hover:bg-accent-500 md:h-10 md:w-10"
      >
        <i className="ri-arrow-right-s-line text-xl" aria-hidden="true" />
      </button>

      <div className="absolute right-4 top-4 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => go(i)}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              i === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/60 hover:bg-background-50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}